import React, { useMemo, useState } from "react";
import "./AuraSosLauncher.css";

export type AuraSosRole = "student" | "parent" | "teacher";

export type AuraSosActivationDraft = {
  role: AuraSosRole;
  category: string;
  description: string;
  created_at: string;
  location?: {
    latitude: number;
    longitude: number;
    accuracy?: number;
  };
};

type Props = {
  role: AuraSosRole;
  compact?: boolean;
  onActivate?: (draft: AuraSosActivationDraft) => Promise<void> | void;
};

const ROLE_COPY: Record<AuraSosRole, { title: string; prompt: string; categories: string[] }> = {
  student: {
    title: "AURA SOS™",
    prompt: "Get immediate support for yourself or someone nearby.",
    categories: [
      "Personal or emotional crisis",
      "Medical emergency",
      "Assault or immediate danger",
      "Campus safety emergency",
      "Other urgent concern",
    ],
  },
  parent: {
    title: "AURA SOS™",
    prompt: "Request urgent help for your child, family, or another person.",
    categories: [
      "Child emotional crisis",
      "Medical emergency",
      "Assault or immediate danger",
      "Missing child concern",
      "Other urgent concern",
    ],
  },
  teacher: {
    title: "AURA SOS™",
    prompt: "Report an urgent student, classroom, or campus emergency.",
    categories: [
      "Student emotional crisis",
      "Medical emergency",
      "Assault or fight",
      "Weapon or active violence",
      "Accident or injury",
      "Fire or campus safety emergency",
      "Other urgent concern",
    ],
  },
};

export default function AuraSosLauncher({ role, compact = false, onActivate }: Props) {
  const copy = ROLE_COPY[role];
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(copy.categories[0]);
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState<AuraSosActivationDraft["location"]>();
  const [locationStatus, setLocationStatus] = useState("Location not requested");
  const [working, setWorking] = useState(false);
  const [notice, setNotice] = useState("");

  const canActivate = useMemo(() => Boolean(category) && !working, [category, working]);

  const requestLocation = () => {
    setLocationStatus("Requesting location…");
    if (!navigator.geolocation) {
      setLocationStatus("Location services are not supported on this device.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
        setLocationStatus(`Location ready • accuracy ${Math.round(position.coords.accuracy)} m`);
      },
      (error) => {
        setLocation(undefined);
        setLocationStatus(error.message || "Location permission was not granted.");
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 15000 },
    );
  };

  const activate = async () => {
    if (!canActivate) return;
    setWorking(true);
    setNotice("");

    const draft: AuraSosActivationDraft = {
      role,
      category,
      description: description.trim(),
      created_at: new Date().toISOString(),
      location,
    };

    try {
      sessionStorage.setItem("aura_sos_pending_activation", JSON.stringify(draft));
      await onActivate?.(draft);

      if (onActivate) {
        // The real ERH activation succeeded. Close the launch modal immediately
        // so the Parent/Teacher can use the portal and the live ERH chat.
        setOpen(false);
        setNotice("");
        setDescription("");
        setLocation(undefined);
        setLocationStatus("Location not requested");
      } else {
        setNotice(
          "AURA SOS interface is ready. Backend incident activation will be connected in the next phase.",
        );
      }
    } catch (error: any) {
      setNotice(error?.message || "AURA SOS could not be activated. Call 911 if there is immediate danger.");
    } finally {
      setWorking(false);
    }
  };

  return (
    <>
      <section className={`aura-launch-card ${compact ? "compact" : ""}`}>
        <div className="aura-launch-eyebrow">EMERGENCY HELP</div>
        <button type="button" className="aura-launch-button" onClick={() => setOpen(true)}>
          <span>AURA</span>
          <strong>SOS</strong>
        </button>
        <div className="aura-launch-title">Get Help Now</div>
        <div className="aura-launch-copy">{copy.prompt}</div>
        <div className="aura-launch-disclaimer">Immediate danger: call 911 now.</div>
      </section>

      {open ? (
        <div className="aura-sos-modal-backdrop" role="presentation" onMouseDown={() => !working && setOpen(false)}>
          <section
            className="aura-sos-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`${copy.title} activation`}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="aura-sos-modal-header">
              <div>
                <div className="aura-sos-modal-kicker">Emergency Detection & Activation</div>
                <h2>{copy.title}</h2>
              </div>
              <button type="button" className="aura-sos-close" onClick={() => !working && setOpen(false)}>
                ✕
              </button>
            </header>

            <div className="aura-sos-warning">
              Use this for urgent situations. If someone is in immediate danger, call 911 while AURA SOS is being activated.
            </div>

            <label className="aura-sos-label" htmlFor={`aura-category-${role}`}>
              What kind of help is needed?
            </label>
            <select
              id={`aura-category-${role}`}
              className="aura-sos-field"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
            >
              {copy.categories.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>

            <label className="aura-sos-label" htmlFor={`aura-description-${role}`}>
              Brief description (optional)
            </label>
            <textarea
              id={`aura-description-${role}`}
              className="aura-sos-field aura-sos-textarea"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Share only the information needed to coordinate help."
              maxLength={500}
            />

            <div className="aura-location-box">
              <div>
                <strong>GPS location</strong>
                <span>{locationStatus}</span>
              </div>
              <button type="button" className="aura-sos-secondary" onClick={requestLocation} disabled={working}>
                {location ? "Refresh Location" : "Share Location"}
              </button>
            </div>

            {notice ? <div className="aura-sos-notice">{notice}</div> : null}

            <div className="aura-sos-modal-actions">
              <button type="button" className="aura-sos-secondary" onClick={() => setOpen(false)} disabled={working}>
                Cancel
              </button>
              <button type="button" className="aura-sos-primary" onClick={() => void activate()} disabled={!canActivate}>
                {working ? "Activating…" : "Activate AURA SOS"}
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </>
  );
}
